import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, ChipEingabe, ChipGroup, Label, Skeleton, formatDatumZeit, toast } from '@elbklar/ui';

import { TextField, TextareaField } from '@/components/Felder';
import { radarApi } from '@/lib/api';
import { kontoUrl } from '@/lib/konto';
import { ganzzahl, hatFehler, hoechstens, ohne, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { RadarKind, RadarSettings } from '@/types';
import { KIND_PLURAL } from '@/features/radar/radarText';

const ARTEN: RadarKind[] = ['foerderung', 'wettbewerb', 'event'];
interface Formular {
  enabled: boolean;
  discovery_enabled: boolean;
  company_profile: string;
  location: string;
  regions: string[];
  topics: string[];
  exclusions: string[];
  categories: RadarKind[];
  min_score: string;
  deadline_warning_weeks: string;
  manual_check_weeks: string;
  discovery_searches_per_category: string;
}

type ZahlFeld = 'min_score' | 'deadline_warning_weeks' | 'manual_check_weeks' | 'discovery_searches_per_category';
/** Felder mit eigener Prüfung; `categories` ist die Auswahl der Arten. */
type Feld = 'location' | 'categories' | ZahlFeld;

function ausEinstellungen(e: RadarSettings): Formular {
  return {
    enabled: e.enabled,
    discovery_enabled: e.discovery_enabled,
    company_profile: e.company_profile,
    location: e.location,
    regions: e.regions,
    topics: e.topics,
    exclusions: e.exclusions,
    categories: e.categories,
    min_score: String(e.min_score),
    deadline_warning_weeks: String(e.deadline_warning_weeks),
    manual_check_weeks: String(e.manual_check_weeks),
    discovery_searches_per_category: String(e.discovery_searches_per_category),
  };
}

/**
 * Die Stellschrauben des Radars.
 *
 * Das Firmenprofil und die Listen sind das, womit der Roboter sucht und
 * bewertet — je genauer, desto weniger Rauschen in der Liste.
 */
export function RadarEinstellungen() {
  const queryClient = useQueryClient();
  const [formular, setFormular] = useState<Formular | null>(null);
  const [fehler, setFehler] = useState<Fehler<Feld>>({});

  const einstellungen = useQuery({
    queryKey: ['radar', 'settings'],
    queryFn: radarApi.settings,
    // Sonst überschreibt ein Tab-Wechsel das halb ausgefüllte Formular.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (einstellungen.data) setFormular(ausEinstellungen(einstellungen.data));
    setFehler({});
  }, [einstellungen.data]);

  useEffect(() => {
    if (einstellungen.isError) toast.error('Radar-Einstellungen konnten nicht geladen werden');
  }, [einstellungen.isError]);

  const speichern = useMutation({
    mutationFn: () => {
      const f = formular!;
      return radarApi.updateSettings({
        enabled: f.enabled,
        discovery_enabled: f.discovery_enabled,
        company_profile: f.company_profile,
        location: f.location.trim(),
        regions: f.regions,
        topics: f.topics,
        exclusions: f.exclusions,
        categories: f.categories,
        min_score: Number(f.min_score),
        deadline_warning_weeks: Number(f.deadline_warning_weeks),
        manual_check_weeks: Number(f.manual_check_weeks),
        discovery_searches_per_category: Number(f.discovery_searches_per_category),
      });
    },
    onSuccess: () => {
      toast.success('Radar-Einstellungen gespeichert');
      void queryClient.invalidateQueries({ queryKey: ['radar'] });
    },
    onError: (fehlschlag: { response?: { data?: { detail?: unknown } } }) =>
      toast.error(
        typeof fehlschlag.response?.data?.detail === 'string'
          ? fehlschlag.response.data.detail
          : 'Speichern fehlgeschlagen — liegen die Zahlen im erlaubten Bereich?'
      ),
  });

  // Grenzen aus RadarSettingsUpdate (finance-api). Die Zahlen gehen immer mit;
  // ein leeres Feld würde zu 0 und beim Mindest-Score still gespeichert.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formular) return;
    const unlesbar = unlesbareFelder(event.currentTarget);
    const zahl = (feld: ZahlFeld, min: number, max: number) =>
      ganzzahl(formular[feld], { min, max, pflicht: true, unlesbar: unlesbar.has(feld) });
    const neu: Fehler<Feld> = {
      location: hoechstens(formular.location, 120),
      categories: formular.categories.length === 0 ? 'Mindestens eine Art wählen' : undefined,
      min_score: zahl('min_score', 0, 100),
      deadline_warning_weeks: zahl('deadline_warning_weeks', 1, 26),
      manual_check_weeks: zahl('manual_check_weeks', 1, 52),
      discovery_searches_per_category: zahl('discovery_searches_per_category', 5, 60),
    };
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate();
  };

  /** Wert setzen und den Fehler des Felds zurücknehmen. */
  const setze = (feld: Exclude<Feld, 'categories'>, wert: string) => {
    if (!formular) return;
    setFormular({ ...formular, [feld]: wert });
    setFehler((f) => ohne(f, feld));
  };

  // Reihenfolge wie in ARTEN, egal in welcher Folge gewählt wurde.
  const artenSetzen = (gewaehlt: string[]) => {
    if (!formular) return;
    setFormular({ ...formular, categories: ARTEN.filter((a) => gewaehlt.includes(a)) });
    setFehler((f) => ohne(f, 'categories'));
  };

  return (
    <div className="max-w-4xl">
      <p className="mb-4 text-muted-foreground">
        Womit das Radar sucht und bewertet. Das Profil liest der Roboter, bevor er
        ein Programm einschätzt — am besten so formuliert, wie man es einem Berater
        erklären würde.
      </p>

      {!formular ? (
        <div className="space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-24" />
        </div>
      ) : (
        <form noValidate onSubmit={absenden} className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Checkbox
              label={<ZweiZeilen titel="Radar eingeschaltet" text="Bekannte Quellen regelmäßig auf Änderungen prüfen" />}
              checked={formular.enabled}
              onChange={(an) => setFormular({ ...formular, enabled: an })}
            />
            <Checkbox
              label={
                <ZweiZeilen titel="Neue Programme suchen" text="Zusätzlich im Netz nach bisher unbekannten Programmen suchen" />
              }
              checked={formular.discovery_enabled}
              onChange={(an) => setFormular({ ...formular, discovery_enabled: an })}
            />
          </div>

          <TextareaField
            label="Firmenprofil"
            rows={5}
            placeholder="Tätigkeit, Größe, Vorhaben …"
            value={formular.company_profile}
            onChange={(e) => setFormular({ ...formular, company_profile: e.target.value })}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Standort"
              placeholder="z. B. Coswig"
              value={formular.location}
              onChange={(e) => setze('location', e.target.value)}
              error={fehler.location}
            />
            <ChipEingabe
              label="Regionen"
              hint="Bund, Länder, EU — wo Programme gelten sollen"
              werte={formular.regions}
              onChange={(regions) => setFormular({ ...formular, regions })}
            />
            <ChipEingabe
              label="Themen"
              hint="Wonach gesucht wird"
              werte={formular.topics}
              onChange={(topics) => setFormular({ ...formular, topics })}
            />
            <ChipEingabe
              label="Ausschlüsse"
              hint="Was von vornherein nicht passt"
              werte={formular.exclusions}
              onChange={(exclusions) => setFormular({ ...formular, exclusions })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Arten</Label>
            <ChipGroup
              multiple
              label="Arten"
              items={ARTEN.map((art) => ({ value: art, label: KIND_PLURAL[art] }))}
              value={formular.categories}
              onChange={artenSetzen}
            />
            {fehler.categories && <p className="t-small text-danger">{fehler.categories}</p>}
          </div>

          <p className="t-small text-muted-foreground">
            Meldungen gehen an alle aktiven Nutzer. Ob per Mail oder nur im Tool,
            stellt jeder selbst in seinen{' '}
            <a
              href={kontoUrl('/benachrichtigungen')}
              className="text-link underline-offset-2 hover:underline"
            >
              Benachrichtigungen
            </a>{' '}
            ein.
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <TextField
              label="Mindest-Score"
              type="number"
              name="min_score"
              min={0}
              max={100}
              hint="Darunter wird nicht aufgenommen"
              value={formular.min_score}
              onChange={(e) => setze('min_score', e.target.value)}
              error={fehler.min_score}
              required
            />
            <TextField
              label="Fristwarnung (Wochen)"
              type="number"
              name="deadline_warning_weeks"
              min={1}
              max={26}
              hint="So früh vor der Frist wird gewarnt"
              value={formular.deadline_warning_weeks}
              onChange={(e) => setze('deadline_warning_weeks', e.target.value)}
              error={fehler.deadline_warning_weeks}
              required
            />
            <TextField
              label="Händische Prüfung (Wochen)"
              type="number"
              name="manual_check_weeks"
              min={1}
              max={52}
              hint="Abstand für unlesbare Quellen"
              value={formular.manual_check_weeks}
              onChange={(e) => setze('manual_check_weeks', e.target.value)}
              error={fehler.manual_check_weeks}
              required
            />
            <TextField
              label="Suchen je Art"
              type="number"
              name="discovery_searches_per_category"
              min={5}
              max={60}
              hint="Suchanfragen pro Suchlauf und Art"
              value={formular.discovery_searches_per_category}
              onChange={(e) => setze('discovery_searches_per_category', e.target.value)}
              error={fehler.discovery_searches_per_category}
              required
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="t-small text-muted-foreground">
              Zuletzt geprüft {formatDatumZeit(einstellungen.data?.last_monitor_at)} · zuletzt
              gesucht {formatDatumZeit(einstellungen.data?.last_discovery_at)}
            </p>
            <Button type="submit" variant="primary" loading={speichern.isPending}>
              Einstellungen speichern
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Label einer Checkbox mit erklärender zweiter Zeile. */
function ZweiZeilen({ titel, text }: { titel: ReactNode; text: ReactNode }) {
  return (
    <span>
      <span className="t-body-strong">{titel}</span>
      <span className="block t-small text-muted-foreground">{text}</span>
    </span>
  );
}
